import { useEffect, useRef } from 'react'
import { registerEntityEditor, type EntityEditor } from '../lib/entityEditorRegistry'

/**
 * Registers a component's entity editor (Skill, Connection, or Knowledge Base
 * source, at Agent or Global scope) into the shared entity-editor registry for
 * as long as the component is mounted.
 *
 * Registers exactly once per mount — not on every render — using a ref to
 * always read the latest `isOpen`/`isDirty`/`markClean` closures, so callers
 * can pass fresh functions on every render without churning the registry
 * (which would be wasteful and could race with an in-flight navigation check).
 */
export function useEntityEditorRegistration(editor: EntityEditor): void {
  const editorRef = useRef(editor)
  editorRef.current = editor

  useEffect(() => {
    return registerEntityEditor({
      isOpen: () => editorRef.current.isOpen(),
      isDirty: () => editorRef.current.isDirty(),
      markClean: () => editorRef.current.markClean(),
    })
    // Intentionally register once per mount; editorRef always holds the latest closures.
  }, [])
}
